// filepath: apps/web/src/features/observability/AlertRuleAuthoringDialog.tsx
//
// FR-UI-DASH-1 AC-2 + AC-2a -- alert-rule authoring modal.
//
// Composes the existing FormulaEditor widget (FR-UI-ADM-3) for the
// expression source. The "Save" action targets
// POST /api/observability/alerts/rules (FR-OBS-4 endpoint; not yet
// shipped -- the endpoint returns 404/501 in v1).
//
// AC-2a: when the persist endpoint returns 404 or 501, the modal
// renders an <InlineFieldError> with the
// "alert-rule persistence will ship with FR-OBS-4" text and does
// NOT close; the validated source string is preserved verbatim so
// the user can copy it.
//
// INV-5: no secret values appear in the payload. The
// `expression` field is the typed expression source only -- the
// alert rule's HMAC signing key (if applicable in FR-OBS-4) is
// server-managed and never crosses this wire.
// FR-UI-NFR-2: no client-side formula evaluation -- the
// FormulaEditor talks to POST /api/formula/validate (FR-VAL-4).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { FormulaEditor } from "@/admin/formula/FormulaEditor";
import { InlineFieldError } from "@/design-system/error-states/InlineFieldError";
import { useAlertRulePersist } from "./queries";
import type { AlertRulePersistError } from "./types";

export interface AlertRuleAuthoringDialogProps {
  onClose: () => void;
  /** Optional: pre-fill the rule name / description / severity. */
  initialRuleName?: string;
}

export function AlertRuleAuthoringDialog({
  onClose,
  initialRuleName = "",
}: AlertRuleAuthoringDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [ruleName, setRuleName] = useState(initialRuleName);
  const [expression, setExpression] = useState("");
  const [severity, setSeverity] = useState<"info" | "warning" | "critical">(
    "warning",
  );
  const [persistError, setPersistError] =
    useState<AlertRulePersistError | null>(null);

  const persist = useAlertRulePersist();

  const onSave = async () => {
    if (!ruleName.trim() || !expression.trim()) {
      return;
    }
    setPersistError(null);
    try {
      await persist.mutateAsync({
        rule_id: crypto.randomUUID(),
        rule_name: ruleName,
        expression,
        severity,
      });
      // On success -- close (FR-OBS-4 endpoint is in production,
      // not the v1 404/501 stub path).
      onClose();
    } catch (err) {
      // AC-2a: 404 / 501 / 503 land here. Render the typed
      // InlineFieldError, preserve the validated source string
      // verbatim so the user can copy it, do NOT close the modal.
      setPersistError(err as AlertRulePersistError);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="observability-alert-rule-title"
      className="observability-alert-rule-dialog"
      data-testid="observability-alert-rule-dialog"
    >
      <header className="observability-alert-rule-dialog-header">
        <h2 id="observability-alert-rule-title">
          {t("observability.dashboard.alert_rule.title", "Author alert rule")}
        </h2>
      </header>

      <label
        htmlFor="observability-alert-rule-name"
        className="observability-alert-rule-label"
      >
        {t("observability.dashboard.alert_rule.name_label", "Rule name")}
      </label>
      <input
        id="observability-alert-rule-name"
        type="text"
        className="observability-alert-rule-input"
        data-testid="observability-alert-rule-name"
        value={ruleName}
        onChange={(e) => setRuleName(e.target.value)}
        maxLength={120}
      />

      <label
        htmlFor="observability-alert-rule-severity"
        className="observability-alert-rule-label"
      >
        {t("observability.dashboard.alert_rule.severity_label", "Severity")}
      </label>
      <select
        id="observability-alert-rule-severity"
        className="observability-alert-rule-select"
        data-testid="observability-alert-rule-severity"
        value={severity}
        onChange={(e) =>
          setSeverity(e.target.value as "info" | "warning" | "critical")
        }
      >
        <option value="info">info</option>
        <option value="warning">warning</option>
        <option value="critical">critical</option>
      </select>

      <div
        className="observability-alert-rule-expression"
        data-testid="observability-alert-rule-expression"
      >
        <FormulaEditor
          value={expression}
          onChange={setExpression}
          fieldPath="observability-alert-rule-expression"
          labelKey="observability.dashboard.alert_rule.expression_label"
        />
      </div>

      {persistError && (
        <InlineFieldError
          id="observability-alert-rule-persist-error"
          message={
            persistError.message ||
            t(
              "observability.dashboard.alert_rule.error.persist_unavailable",
              "alert-rule persistence will ship with FR-OBS-4",
            )
          }
        />
      )}

      <footer className="observability-alert-rule-dialog-footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
          data-testid="observability-alert-rule-cancel"
        >
          {t("common.cancel", "Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            void onSave();
          }}
          disabled={persist.isPending}
          data-testid="observability-alert-rule-save"
        >
          {persist.isPending
            ? t("observability.dashboard.alert_rule.saving", "Saving…")
            : t("observability.dashboard.alert_rule.save", "Save")}
        </button>
      </footer>
    </div>
  );
}
